import React, { useState, useEffect, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import { TipoMarcacion, MarcacionConfigFE, MarcacionOpcionFE, MarcacionConfigResponse, EsquemaMarcacion } from '../types';
import { cotizacionDetalleService } from '../service';
import { calcularPrecioMarcacion, calcularPrecioUnitario, seleccionarOpcion, esTipoManual } from '../utils/calcularPrecioMarcacion';

interface ProveedorMarcacion {
  id: number;
  nombre: string;
}

interface MarcacionModalProps {
  open: boolean;
  tipoMarcacion: TipoMarcacion;
  tiposMarcacion: TipoMarcacion[];
  cantidad: number;
  precioActual: number | null;
  opcionActualId: number | null;
  proveedorMarcacionActualId: number | null;
  // SPEC-152 — proveedor del producto de la línea; condiciona la aparición de "Catálogos
  // Promocionales" en la lista de proveedores de marcación (RN-001). Null si el producto
  // no tiene proveedor (RN-004).
  productoProveedorId: number | null;
  onConfirm: (
    tipoId: number,
    proveedorId: number,
    opcionId: number | null,
    precio: number,
    esquema: EsquemaMarcacion,
    opcionesFiltradas: MarcacionOpcionFE[],
  ) => void;
  onCancel: () => void;
}

// SPEC-096 (RN-003, diseño sección 3): única fuente de verdad del esquema de una marcación
// confirmada — el hook nunca re-deriva, solo consume lo que el modal ya calculó.
export function derivarEsquema(
  opcion: MarcacionOpcionFE | null,
  esManual: boolean,
  areaCm2?: number,
): EsquemaMarcacion {
  if (esManual) return 'manual';
  if (areaCm2 !== undefined && areaCm2 > 0) return 'area';
  if (opcion && opcion.cantidadMin === null) return 'minima';
  return 'rango';
}

const selectSx = {
  height: '44px',
  fontSize: '15px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DFDCE2' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9B2B9D' },
  minWidth: 220,
};

export function MarcacionModal({
  open,
  tipoMarcacion,
  tiposMarcacion,
  cantidad,
  precioActual,
  opcionActualId,
  proveedorMarcacionActualId,
  productoProveedorId,
  onConfirm,
  onCancel,
}: MarcacionModalProps) {
  // RN-003: tipo seleccionado dentro del modal, inicializado desde el prop `tipoMarcacion`
  // pero editable internamente sin cerrar el modal.
  const [tipoSeleccionado, setTipoSeleccionado] = useState<TipoMarcacion>(tipoMarcacion);

  const [cargandoProveedores, setCargandoProveedores] = useState(false);
  const [proveedores, setProveedores] = useState<ProveedorMarcacion[]>([]);
  const [proveedorSeleccionadoId, setProveedorSeleccionadoId] = useState<number | ''>('');

  const [cargando, setCargando] = useState(false);
  const [configData, setConfigData] = useState<MarcacionConfigResponse | null>(null);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState<string>('');
  const [tintasSeleccionadas, setTintasSeleccionadas] = useState<number | null>(null);
  const [precioManual, setPrecioManual] = useState<string>('');
  const [areaCm2, setAreaCm2] = useState<string>('');

  const esManual = esTipoManual(tipoSeleccionado.nombre);
  // RN-003: si el tipo activo dentro del modal ya no es el que llegó por prop,
  // la precarga (proveedor/categoría/tintas/precio) del tipo original no aplica.
  const tipoCambiadoInternamente = tipoSeleccionado.id !== tipoMarcacion.id;

  const handleTipoChange = (e: SelectChangeEvent<string>) => {
    const nuevoId = Number(e.target.value);
    const nuevoTipo = tiposMarcacion.find(t => t.id === nuevoId);
    if (!nuevoTipo) return;
    setTipoSeleccionado(nuevoTipo);
  };

  // Cargar proveedores al abrir el modal o al cambiar el tipo seleccionado
  useEffect(() => {
    if (!open) return;
    setCargandoProveedores(true);
    setProveedores([]);
    setConfigData(null);
    setCategoriaSeleccionada('');
    setTintasSeleccionadas(null);
    // SPEC-147 RN-004: precioActual es siempre unitario; el campo digitado se precarga con el
    // unitario almacenado directamente (no × cantidad), porque el valor digitado ES el unitario.
    // RN-003: si el tipo cambió dentro del modal, no hay precio previo que reconstruir
    setPrecioManual(!tipoCambiadoInternamente && precioActual ? String(precioActual) : '');
    setAreaCm2('');

    cotizacionDetalleService
      .obtenerProveedoresMarcacion(tipoSeleccionado.id, productoProveedorId)
      .then(lista => {
        setProveedores(lista);
        // Pre-seleccionar proveedor previo si existe en la lista (solo si no cambió el tipo)
        if (!tipoCambiadoInternamente && proveedorMarcacionActualId && lista.some(p => p.id === proveedorMarcacionActualId)) {
          setProveedorSeleccionadoId(proveedorMarcacionActualId);
        } else {
          setProveedorSeleccionadoId('');
        }
      })
      .finally(() => setCargandoProveedores(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tipoSeleccionado.id]);

  // Cargar config del proveedor seleccionado (solo para tipos no-manuales)
  useEffect(() => {
    if (!open || esManual || proveedorSeleccionadoId === '') {
      setConfigData(null);
      setCategoriaSeleccionada('');
      setTintasSeleccionadas(null);
      return;
    }
    setCargando(true);
    setConfigData(null);
    setCategoriaSeleccionada('');
    setTintasSeleccionadas(null);
    cotizacionDetalleService
      .obtenerConfigMarcacion(proveedorSeleccionadoId as number, tipoSeleccionado.id)
      .then(data => {
        setConfigData(data);
        // RN-002: precargar categoría/tintas desde la opción ya confirmada (solo si no cambió el tipo)
        if (data && !tipoCambiadoInternamente && opcionActualId !== null) {
          const opcionPrevia = data.opciones.find(o => o.id === opcionActualId);
          if (opcionPrevia) {
            if (opcionPrevia.categoria) setCategoriaSeleccionada(opcionPrevia.categoria);
            if (data.config.tipoVariable === 'por_tinta' && opcionPrevia.numTintas !== null) {
              setTintasSeleccionadas(opcionPrevia.numTintas);
            }
          }
        }
      })
      .finally(() => setCargando(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proveedorSeleccionadoId, tipoSeleccionado.id, esManual, opcionActualId]);

  const tipoVariable: MarcacionConfigFE['tipoVariable'] | 'valor_manual' =
    configData?.config.tipoVariable ?? 'valor_manual';

  const categorias = useMemo(() => {
    if (!configData) return [];
    const set = new Set<string>();
    configData.opciones.forEach(o => { if (o.categoria) set.add(o.categoria); });
    return Array.from(set);
  }, [configData]);

  const tintasDisponibles = useMemo(() => {
    if (!configData || !categoriaSeleccionada || tipoVariable !== 'por_tinta') return [];
    const set = new Set<number>();
    configData.opciones
      .filter(o => o.categoria === categoriaSeleccionada && o.numTintas !== null)
      .forEach(o => set.add(o.numTintas!));
    return Array.from(set).sort((a, b) => a - b);
  }, [configData, categoriaSeleccionada, tipoVariable]);

  const opcionesFiltradas = useMemo((): MarcacionOpcionFE[] => {
    if (!configData) return [];
    let ops = configData.opciones;
    if (categoriaSeleccionada) ops = ops.filter(o => o.categoria === categoriaSeleccionada);
    if (tipoVariable === 'por_tinta' && tintasSeleccionadas !== null) {
      ops = ops.filter(o => o.numTintas === tintasSeleccionadas);
    }
    return ops;
  }, [configData, categoriaSeleccionada, tintasSeleccionadas, tipoVariable]);

  const opcionAuto = useMemo(() => {
    if (esManual || tipoVariable === 'valor_manual' || !configData) return null;
    if (tipoVariable === 'por_area' && categoriaSeleccionada === 'Impresión') return null;
    return seleccionarOpcion(opcionesFiltradas, cantidad);
  }, [opcionesFiltradas, cantidad, tipoVariable, categoriaSeleccionada, configData, esManual]);

  // RN-004: el total calculado siempre representa el costo del trabajo completo
  // (para toda la cantidad de la línea); el precio mostrado/guardado es unitario.
  const precioTotalCalculado = useMemo((): number => {
    if (esManual || tipoVariable === 'valor_manual') {
      return parseInt(precioManual.replace(/\D/g, ''), 10) || 0;
    }
    if (tipoVariable === 'por_area' && categoriaSeleccionada === 'Impresión') {
      const area = parseFloat(areaCm2);
      if (!area || area <= 0) return 0;
      const tier = area <= 9000
        ? opcionesFiltradas.find(o => o.cantidadMin !== null && o.cantidadMax !== null)
        : opcionesFiltradas.find(o => o.cantidadMin !== null && o.cantidadMax === null);
      if (!tier) return Math.max(88000, area * 1.87);
      return calcularPrecioMarcacion(tier, 1, area);
    }
    if (!opcionAuto) return 0;
    if (opcionAuto.esCotizar) return parseInt(precioManual.replace(/\D/g, ''), 10) || 0;
    return calcularPrecioMarcacion(opcionAuto, cantidad);
  }, [esManual, tipoVariable, precioManual, opcionAuto, cantidad, categoriaSeleccionada, areaCm2, opcionesFiltradas]);

  // SPEC-147 RN-001: esquema de precio digitado = el usuario escribe el precio en un campo
  // (Láser/Sublimación manual, tipos sin config `valor_manual`, o fila COTIZAR).
  const esPrecioDigitado = esManual || tipoVariable === 'valor_manual' || opcionAuto?.esCotizar === true;

  // SPEC-147: el valor que se persiste/muestra es el UNITARIO.
  // - Esquema digitado (RN-001): el número escrito ES el unitario, sin dividir por la cantidad.
  // - Esquema calculado (RN-005): se conserva la ruta actual (total por tarifa/área ÷ cantidad).
  const precioCalculado = useMemo(() => {
    if (esPrecioDigitado) {
      return parseInt(precioManual.replace(/\D/g, ''), 10) || 0;
    }
    return calcularPrecioUnitario(precioTotalCalculado, cantidad);
  }, [esPrecioDigitado, precioManual, precioTotalCalculado, cantidad]);

  const esCotizarActivo = opcionAuto?.esCotizar === true;
  const puedeConfirmar = precioCalculado > 0 && proveedorSeleccionadoId !== '';

  const handleConfirmar = () => {
    if (proveedorSeleccionadoId === '') return;
    const areaCm2Numero = parseFloat(areaCm2);
    const esquema = derivarEsquema(
      opcionAuto,
      esManual,
      tipoVariable === 'por_area' && categoriaSeleccionada === 'Impresión' ? areaCm2Numero : undefined,
    );
    onConfirm(
      tipoSeleccionado.id,
      proveedorSeleccionadoId as number,
      opcionAuto?.id ?? null,
      precioCalculado,
      esquema,
      opcionesFiltradas,
    );
  };

  const renderTierTable = () => {
    if (opcionesFiltradas.length === 0) return null;
    return (
      <Box sx={{ mt: 2, border: '1px solid #E8E6EB', borderRadius: '8px', overflow: 'hidden' }}>
        {opcionesFiltradas.map(op => {
          const esActiva = op.id === opcionAuto?.id;
          return (
            <Box
              key={op.id}
              data-testid={`tier-row-${op.id}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                px: 2,
                py: 1.2,
                background: esActiva ? '#F4EAF4' : '#FFFFFF',
                borderBottom: '1px solid #F0ECF5',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#32164B', fontWeight: esActiva ? 600 : 400 }}>
                {op.etiquetaCantidad ?? op.categoria ?? '—'}
                {op.esCotizar && ' (COTIZAR)'}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: esActiva ? '#9B2B9D' : '#6B6472', fontWeight: esActiva ? 700 : 400 }}>
                {op.esCotizar ? '—' : `$${op.precio.toLocaleString('es-CO')}`}
                {op.cantidadMin !== null && !op.esCotizar ? '/u' : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderSelectorProveedor = () => {
    if (cargandoProveedores) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} sx={{ color: '#9B2B9D' }} />
        </Box>
      );
    }
    if (proveedores.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '14px', color: '#6B6472', mb: 0.5 }}>
          Proveedor de marcación
        </Typography>
        <Select
          data-testid="select-proveedor-marcacion"
          value={proveedorSeleccionadoId}
          onChange={e => setProveedorSeleccionadoId(e.target.value as number)}
          displayEmpty
          sx={{ ...selectSx, width: '100%' }}
        >
          <MenuItem value=""><em>Seleccionar proveedor...</em></MenuItem>
          {proveedores.map(p => (
            <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  const renderCondiciones = () => {
    if (cargandoProveedores) return null;
    // RN-006 (SPEC-085): si el tipo no tiene proveedores configurados, nunca habrá
    // un proveedor que auto-seleccionar, así que el campo manual se muestra directo.
    const sinProveedoresConfigurados = proveedores.length === 0;
    if (!sinProveedoresConfigurados && proveedorSeleccionadoId === '') return null;

    // Tipos manuales (laser/sublimacion) o sin config → campo manual
    if (sinProveedoresConfigurados || esManual || (!cargando && !configData)) {
      return (
        <Box>
          <Typography sx={{ fontSize: '14px', color: '#6B6472', mb: 1 }}>
            Costo unitario de marcación ($)
          </Typography>
          <TextField
            data-testid="input-precio-manual"
            fullWidth
            placeholder="Ej: 75000"
            value={precioManual}
            onChange={e => setPrecioManual(e.target.value.replace(/\D/g, ''))}
            inputProps={{ inputMode: 'numeric' }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '48px',
                '& fieldset': { borderColor: '#DFDCE2' },
                '&:hover fieldset': { borderColor: '#9B2B9D' },
              },
            }}
          />
        </Box>
      );
    }

    if (cargando) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress sx={{ color: '#9B2B9D' }} />
        </Box>
      );
    }

    if (tipoVariable === 'por_tinta' || tipoVariable === 'por_material') {
      return (
        <Box>
          <Typography sx={{ fontSize: '14px', color: '#6B6472', mb: 0.5 }}>Categoría</Typography>
          <Select
            data-testid="select-categoria"
            value={categoriaSeleccionada}
            onChange={e => { setCategoriaSeleccionada(e.target.value); setTintasSeleccionadas(null); }}
            displayEmpty
            sx={{ ...selectSx, width: '100%', mb: 2 }}
          >
            <MenuItem value=""><em>Seleccionar...</em></MenuItem>
            {categorias.map(cat => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>

          {tipoVariable === 'por_tinta' && categoriaSeleccionada && (
            <>
              <Typography sx={{ fontSize: '14px', color: '#6B6472', mb: 0.5 }}>Número de tintas</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {tintasDisponibles.map(t => (
                  <Chip
                    key={t}
                    label={t}
                    data-testid={`chip-tinta-${t}`}
                    onClick={() => setTintasSeleccionadas(t)}
                    sx={{
                      fontWeight: tintasSeleccionadas === t ? 700 : 400,
                      background: tintasSeleccionadas === t ? '#9B2B9D' : '#F0ECF5',
                      color: tintasSeleccionadas === t ? '#FFFFFF' : '#32164B',
                      cursor: 'pointer',
                      '&:hover': { background: tintasSeleccionadas === t ? '#7A2280' : '#E4D9F0' },
                    }}
                  />
                ))}
              </Box>
            </>
          )}

          {categoriaSeleccionada && (tipoVariable === 'por_material' || tintasSeleccionadas !== null) && renderTierTable()}

          {esCotizarActivo && (
            <>
              <Alert severity="info" sx={{ mt: 2, fontSize: '13px' }}>
                Este caso debe cotizarse directamente con el proveedor
              </Alert>
              <TextField
                data-testid="input-precio-cotizar"
                fullWidth
                placeholder="Ingrese el precio acordado"
                value={precioManual}
                onChange={e => setPrecioManual(e.target.value.replace(/\D/g, ''))}
                inputProps={{ inputMode: 'numeric' }}
                sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { height: '48px' } }}
              />
            </>
          )}
        </Box>
      );
    }

    if (tipoVariable === 'por_tiempo') {
      return (
        <Box>
          <Typography sx={{ fontSize: '14px', color: '#6B6472', mb: 1 }}>Rango de tiempo</Typography>
          <Box sx={{ border: '1px solid #E8E6EB', borderRadius: '8px', overflow: 'hidden' }}>
            {categorias.map(cat => {
              const esActiva = cat === categoriaSeleccionada;
              const opCat = opcionesFiltradas.find(o => o.categoria === cat);
              const esCot = opCat?.esCotizar ?? false;
              return (
                <Box
                  key={cat}
                  data-testid={`tiempo-opcion-${cat.replace(/\s+/g, '-')}`}
                  onClick={() => setCategoriaSeleccionada(cat)}
                  sx={{
                    px: 2, py: 1.5, cursor: 'pointer',
                    background: esActiva ? '#F4EAF4' : '#FFFFFF',
                    borderBottom: '1px solid #F0ECF5',
                    '&:last-child': { borderBottom: 'none' },
                    '&:hover': { background: '#FAF5FA' },
                  }}
                >
                  <Typography sx={{ fontSize: '15px', fontWeight: esActiva ? 600 : 400, color: '#32164B' }}>
                    {cat}{esCot ? ' — COTIZAR' : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {categoriaSeleccionada && renderTierTable()}
          {esCotizarActivo && (
            <>
              <Alert severity="info" sx={{ mt: 2, fontSize: '13px' }}>
                Este caso debe cotizarse directamente con el proveedor
              </Alert>
              <TextField
                data-testid="input-precio-cotizar"
                fullWidth
                placeholder="Ingrese el precio acordado"
                value={precioManual}
                onChange={e => setPrecioManual(e.target.value.replace(/\D/g, ''))}
                inputProps={{ inputMode: 'numeric' }}
                sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { height: '48px' } }}
              />
            </>
          )}
        </Box>
      );
    }

    if (tipoVariable === 'por_area') {
      return (
        <Box>
          <Typography sx={{ fontSize: '14px', color: '#6B6472', mb: 0.5 }}>Tipo de sublimación</Typography>
          <Select
            data-testid="select-categoria"
            value={categoriaSeleccionada}
            onChange={e => setCategoriaSeleccionada(e.target.value)}
            displayEmpty
            sx={{ ...selectSx, width: '100%', mb: 2 }}
          >
            <MenuItem value=""><em>Seleccionar...</em></MenuItem>
            {categorias.map(cat => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>

          {categoriaSeleccionada === 'Impresión' && (
            <>
              <Typography sx={{ fontSize: '14px', color: '#6B6472', mb: 0.5 }}>Área a imprimir (cm²)</Typography>
              <TextField
                data-testid="input-area-cm2"
                fullWidth
                placeholder="Ej: 500"
                value={areaCm2}
                onChange={e => setAreaCm2(e.target.value.replace(/[^0-9.]/g, ''))}
                inputProps={{ inputMode: 'decimal' }}
                sx={{ '& .MuiOutlinedInput-root': { height: '48px' } }}
              />
            </>
          )}

          {categoriaSeleccionada && categoriaSeleccionada !== 'Impresión' && renderTierTable()}
        </Box>
      );
    }

    // fallback → campo manual
    return (
      <Box>
        <Typography sx={{ fontSize: '14px', color: '#6B6472', mb: 1 }}>
          Costo unitario de marcación ($)
        </Typography>
        <TextField
          data-testid="input-precio-manual"
          fullWidth
          placeholder="Ej: 75000"
          value={precioManual}
          onChange={e => setPrecioManual(e.target.value.replace(/\D/g, ''))}
          inputProps={{ inputMode: 'numeric' }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '48px',
              '& fieldset': { borderColor: '#DFDCE2' },
              '&:hover fieldset': { borderColor: '#9B2B9D' },
            },
          }}
        />
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      data-testid="marcacion-modal"
    >
      <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '17px', color: '#32164B', pb: 1 }}>
        {tipoSeleccionado.nombre}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {tiposMarcacion.length > 1 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '14px', color: '#6B6472', mb: 0.5 }}>
              Tipo de marcación
            </Typography>
            <Select
              data-testid="select-tipo-marcacion-modal"
              value={String(tipoSeleccionado.id)}
              onChange={handleTipoChange}
              sx={{ ...selectSx, width: '100%' }}
            >
              {tiposMarcacion.map(t => (
                <MenuItem key={t.id} value={String(t.id)}>{t.nombre}</MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {renderSelectorProveedor()}

        {renderCondiciones()}

        {precioCalculado > 0 && proveedorSeleccionadoId !== '' && (
          <Box
            data-testid="precio-calculado-preview"
            sx={{ mt: 2.5, p: 2, background: '#F4EAF4', borderRadius: '8px' }}
          >
            <Typography sx={{ fontSize: '14px', color: '#6B6472' }}>Precio de marcación calculado</Typography>
            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#9B2B9D' }}>
              ${precioCalculado.toLocaleString('es-CO')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          data-testid="btn-cancelar-marcacion"
          onClick={onCancel}
          variant="outlined"
          sx={{ borderColor: '#DFDCE2', color: '#6B6472', textTransform: 'none', fontWeight: 500 }}
        >
          Cancelar
        </Button>
        <Button
          data-testid="btn-confirmar-marcacion"
          onClick={handleConfirmar}
          disabled={!puedeConfirmar}
          title={!puedeConfirmar ? 'Selecciona un proveedor e ingresa un precio mayor a $0' : undefined}
          variant="contained"
          sx={{
            background: '#9B2B9D',
            '&:hover': { background: '#7A2280' },
            '&.Mui-disabled': { background: '#E4D9F0', color: '#A898B8' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Confirmar precio: ${precioCalculado.toLocaleString('es-CO')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
